import { MentionSearchQueryModel } from '../../Monitor/Shared/models/mention-search-query.model';
import { BasicNumberFilterModel } from '../../api/mentions/model/basic-number-filter.model';

/** The filters that scope a search to one agent, as advanced monitoring names them */
type AgentScopeField = 'aiAgent' | 'aiAgentSource' | 'aiAgentAdhoc';

const agentScopeFields: AgentScopeField[] = ['aiAgent', 'aiAgentSource', 'aiAgentAdhoc'];

/**
 * How the left hand panel narrows an agent, as advanced monitoring's source and ad hoc filters. Null
 * while nothing is narrowed - everything ticked, or the panel has not published yet - which leaves
 * just the agent filter.
 *
 * With Other ticked the narrowing is expressed as exclusions of what was unticked, since Other's
 * mentions carry source ids no longer listed anywhere and so can't be included by id. With Other
 * unticked it is inclusions of what is still ticked - so a source removed from the agent drops out
 * even though its id stays on the mentions it saved. Advanced monitoring ORs the two include lists,
 * since a mention carries a source id or an ad hoc id, never both, and ANDs exclusions.
 */
export interface AgentScopeModel {
    sources: BasicNumberFilterModel;
    adHocRuns: BasicNumberFilterModel;
}

/** One filter's two lists, as the query model holds them */
interface AgentScopeFilter {
    include: number[];
    exclude: number[];
}

/**
 * Narrows a query to one agent, and within it to the scope the left hand panel has published.
 *
 * Returns whether anything actually changed, so a caller that re-runs its search off the back of
 * this only does so when there is something new to search for.
 */
export function applyAgentScope(query: MentionSearchQueryModel, agentId: number, agentScope: AgentScopeModel): boolean {
    const scope: Record<AgentScopeField, AgentScopeFilter> = {
        aiAgent: { include: agentId ? [agentId] : [], exclude: [] },
        aiAgentSource: {
            include: agentScope?.sources.include.slice() ?? [],
            exclude: agentScope?.sources.exclude.slice() ?? []
        },
        aiAgentAdhoc: {
            include: agentScope?.adHocRuns.include.slice() ?? [],
            exclude: agentScope?.adHocRuns.exclude.slice() ?? []
        }
    };

    const unchanged = agentScopeFields.every(field =>
        (query[field]?.include ?? []).join() === scope[field].include.join()
        && (query[field]?.exclude ?? []).join() === scope[field].exclude.join());

    if (unchanged) {
        return false;
    }

    agentScopeFields.forEach(field => query[field] = scope[field]);

    return true;
}
